import React, { Component, ErrorInfo, ReactNode } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useGetSharedFunctionality } from './api/lending/lapiHooks';
import { WhiteLabelProvider } from './Buyer/store/WhiteLabelProvider';
import { ImpersonationBanner } from './components/ImpersonationPip/ImpersonationPip';
import { useOnce } from './hooks/useOnce';
import { useQueryParam } from './hooks/useQueryParam';
import { GenericError } from './Pages/FailurePages/GenericError';
import { IwQueryClientProvider } from './store/IwQueryClientProvider';
import { Toaster } from './store/IwToast';
import {
  registerSharedFunctionalities,
  registerStateKey,
  SharedFunctionality,
} from './utils/fullStory';
import logger from './utils/logger';

export const AppWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <IwQueryClientProvider>
      <UserIdentifier />
      <ImpersonationBanner />
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
      <ErrorBoundary>
        <WhiteLabelProvider>{children}</WhiteLabelProvider>
      </ErrorBoundary>
    </IwQueryClientProvider>
  );
};

const UserIdentifier = () => {
  const functionalitiesToRegister: SharedFunctionality[] = [
    'is_non_linear_security_checks_enabled',
  ];

  const { stateKey, functionality, loadingFunctionality } =
    useGetSharedFunctionality();
  const impersonationStateKey = useQueryParam('state_key');

  useOnce({
    conditionFn: !loadingFunctionality && !!stateKey,
    fn: () => {
      // Register user with Amplitude & FullStory.
      registerStateKey(stateKey!);
      tracking.identifyCustomer({
        stateKey: stateKey!,
        isImpersonating: !!impersonationStateKey,
        country: 'UK',
      });

      if (functionality) {
        // Filter out shared functionalities we do not wish to register with Amplitude & FullStory.
        const functionalities = Object.fromEntries(
          Object.entries(functionality).filter(([name]) =>
            functionalitiesToRegister.includes(name as SharedFunctionality),
          ),
        );

        if (!Object.keys(functionalities).length) return;

        // Register shared functionalities with FullStory & Amplitude.
        registerSharedFunctionalities(stateKey!, functionalities);
        tracking.ampli.identify(stateKey!, {
          // @ts-ignore
          'Split tests': Object.entries(functionalities).map(
            ([name, value]) => `iwocapay_${name}=${value}`,
          ),
        });
      }
    },
  });

  return null;
};

interface Props {
  children: ReactNode;
  errorComponent: ReactNode;
}

interface State {
  hasError: boolean;
}

export class CustomErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error(error.toString(), { stack: error.stack, ...info });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.errorComponent;
    }

    return this.props.children;
  }
}

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  return (
    <CustomErrorBoundary
      errorComponent={<GenericError errorText="something went wrong." />}
    >
      {children}
    </CustomErrorBoundary>
  );
};
